import { Link } from "@tanstack/react-router";
import { ArrowRight, BadgeCheck, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PeopelsLogo } from "@/components/brand/PeopelsLogo";

export function Footer() {
  return (
    <footer className="border-t border-border bg-surface-muted">
      {/* High-intent banner — last conversion shot */}
      <div className="border-b border-border bg-gradient-hero text-primary-foreground">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-4 py-8 sm:flex-row sm:items-center sm:px-6 lg:px-8">
          <div>
            <p className="font-display text-xl font-bold sm:text-2xl">Ready to get it done?</p>
            <p className="text-sm text-white/75">Free to post, matched with vetted local Helpers, and your payment is protected in escrow until the job is done.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild size="lg" className="rounded-xl bg-white text-secondary shadow-elevated hover:bg-white/90">
              <Link to="/post-job">Post a job free <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl border-white/30 bg-transparent text-white hover:bg-white/10">
              <Link to="/become-helper">Become a Helper</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Link to="/" aria-label="Peopels home" className="inline-flex items-center rounded-lg bg-[#0B132B] px-4 py-3">
              <PeopelsLogo variant="footer" height={48} loading="lazy" />
            </Link>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground">
              A new London marketplace to hire vetted local Helpers: handyman, TV mounting, furniture assembly and moving help. ID-verified and reviewed.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground">
                <BadgeCheck className="h-3 w-3 text-emerald-600" /> KYC verified
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground">
                <Shield className="h-3 w-3 text-emerald-600" /> Escrow payments
              </span>
            </div>
          </div>

          <FooterCol title="Get help" links={[
            { to: "/post-job", label: "Post a job" },
            { to: "/browse", label: "Browse Helpers" },
            { to: "/categories", label: "All categories" },
            { to: "/how-it-works", label: "How it works" },
          ]} />
          <FooterCol title="Pricing & trust" links={[
            { to: "/how-it-works", label: "Pricing & fees" },
            { to: "/how-it-works", label: "Happiness guarantee" },
            { to: "/contact", label: "Trust & safety" },
          ]} />
          <FooterCol title="For Helpers" links={[
            { to: "/become-helper", label: "Become a Helper" },
            { to: "/helper/kyc", label: "Verification (KYC)" },
            { to: "/helper/offers", label: "Helper dashboard" },
          ]} />
          <FooterCol title="Support" links={[
            { to: "/contact", label: "Contact support" },
            { to: "/about", label: "About us" },
            { to: "/privacy", label: "Privacy" },
            { to: "/terms", label: "Terms" },
            { to: "/cookies", label: "Cookies" },
          ]} />
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-border pt-6 text-sm text-muted-foreground sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} Peopels Ltd. All rights reserved.</p>
          <p>Made with care in the UK 🇬🇧</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: { to: string; label: string }[] }) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
      <ul className="space-y-2 text-sm">
        {links.map((l) => (
          <li key={`${l.to}-${l.label}`}>
            <Link to={l.to} className="text-muted-foreground transition-colors hover:text-foreground">{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
